import type { AxiosInstance } from 'axios';

import type { settlements } from '@terra/contracts';

export type SettlementSchedule = 'T_PLUS_1' | 'T_PLUS_7' | 'T_PLUS_14' | 'MANUAL';
export type BankAccountStatus = 'PENDING' | 'VERIFIED' | 'REJECTED' | 'DISABLED';
export type SettlementStatus =
  /** Delivered, but the courier still holds the customer's cash — nothing is owed yet. */
  'PENDING_COD_REMITTANCE' | 'PENDING_REFUND_WINDOW' | 'ELIGIBLE' | 'SETTLED' | 'HELD' | 'REVERSED';
export type PayoutStatus = 'PENDING' | 'APPROVED' | 'RELEASED' | 'REJECTED' | 'FAILED';

export interface VendorBankAccount {
  id: string;
  vendorId: string;
  accountHolderName: string;
  accountNumberMasked: string;
  ifsc: string;
  isDefault: boolean;
  status: BankAccountStatus;
  verifiedAt: string | null;
  createdAt: string;
}

export interface Settlement {
  id: string;
  subOrderId: string;
  vendorId: string;
  status: SettlementStatus;
  grossPaise: number;
  commissionPaise: number;
  /** Terra Shoppy's share of offer discounts, paid on top of the sale. */
  platformSupportPaise?: number;
  tcsPaise: number;
  tdsPaise: number;
  /** Courier freight charged to the seller. */
  shippingFeePaise?: number;
  reserveHeldPaise: number;
  netPayablePaise: number;
  reserveReleaseAt: string | null;
  eligibleAt: string | null;
  settledAt: string | null;
  ledgerTxId: string | null;
  notes: string | null;
  createdAt: string;
}

export interface PayoutRequest {
  id: string;
  vendorId: string;
  bankAccountId: string | null;
  runId: string | null;
  amountPaise: number;
  status: PayoutStatus;
  provider: string | null;
  providerPayoutId: string | null;
  providerStatus: string | null;
  failureReason: string | null;
  bankRef: string | null;
  note: string | null;
  requestedAt: string;
  approvedAt: string | null;
  releasedAt: string | null;
}

export interface VendorPayoutEligibility {
  vendorId: string;
  receivablePaise: number;
  heldPaise: number;
  withdrawablePaise: number;
  payoutThresholdPaise: number;
  eligibleForAutoPayout: boolean;
}

export interface SettlementRunSummary {
  id: string;
  runDate: string;
  status: string;
  totals: {
    vendorCount: number;
    subOrderCount: number;
    grossPaise: number;
    commissionPaise: number;
    tcsPaise: number;
    tdsPaise: number;
    reservePaise: number;
    netPaise: number;
    payoutCount: number;
  } | null;
  startedAt: string;
  completedAt: string | null;
}

export interface AddBankAccountRequest {
  accountHolderName: string;
  accountNumber: string;
  ifsc: string;
  isDefault?: boolean;
}

export interface UpdateVendorSettlementConfigRequest {
  settlementSchedule?: SettlementSchedule;
  rollingReserveBps?: number;
  rollingReserveDays?: number;
  payoutThresholdPaise?: number;
  panNumber?: string;
  payoutFrozen?: boolean;
}

export function settlementsEndpoints(http: AxiosInstance) {
  return {
    // Vendor self
    myEligibility: () =>
      http.get<VendorPayoutEligibility>('/settlements/vendor/me/eligibility').then((r) => r.data),
    mySettlements: () =>
      http.get<{ data: Settlement[] }>('/settlements/vendor/me').then((r) => r.data.data),
    myBankAccounts: () =>
      http
        .get<{ data: VendorBankAccount[] }>('/settlements/vendor/me/bank-accounts')
        .then((r) => r.data.data),
    /** Make one of the vendor's verified accounts the default destination. */
    updateMyBankAccount: (accountId: string, body: { isDefault: true }) =>
      http
        .patch<VendorBankAccount>(`/settlements/vendor/me/bank-accounts/${accountId}`, body)
        .then((r) => r.data),
    /** Take an account out of the list. Refused while a payout is in flight. */
    deleteMyBankAccount: (accountId: string) =>
      http
        .delete<{ removed: true }>(`/settlements/vendor/me/bank-accounts/${accountId}`)
        .then((r) => r.data),
    addMyBankAccount: (body: AddBankAccountRequest) =>
      http.post('/settlements/vendor/me/bank-accounts', body).then((r) => r.data),
    myPayouts: () =>
      http
        .get<{ data: PayoutRequest[] }>('/settlements/vendor/me/payouts')
        .then((r) => r.data.data),
    requestPayout: (body: { amountPaise: number; bankAccountId?: string; note?: string }) =>
      http.post('/settlements/vendor/me/payouts', body).then((r) => r.data),

    // Admin
    adminEligibility: (vendorId: string) =>
      http
        .get<VendorPayoutEligibility>(`/admin/settlements/vendors/${vendorId}/eligibility`)
        .then((r) => r.data),
    adminSettlements: (vendorId: string) =>
      http
        .get<{ data: Settlement[] }>(`/admin/settlements/vendors/${vendorId}`)
        .then((r) => r.data.data),
    adminPayouts: (vendorId: string) =>
      http
        .get<{ data: PayoutRequest[] }>(`/admin/settlements/vendors/${vendorId}/payouts`)
        .then((r) => r.data.data),
    updateVendorConfig: (vendorId: string, body: UpdateVendorSettlementConfigRequest) =>
      http.patch(`/admin/settlements/vendors/${vendorId}/config`, body).then((r) => r.data),
    /** A seller's bank accounts with full numbers, for verification and manual transfer. */
    adminBankAccounts: (vendorId: string) =>
      http
        .get<{
          data: settlements.AdminVendorBankAccount[];
        }>(`/admin/settlements/vendors/${vendorId}/bank-accounts`)
        .then((r) => r.data.data),
    verifyBankAccount: (accountId: string) =>
      http.post(`/admin/settlements/bank-accounts/${accountId}/verify`).then((r) => r.data),
    /** Settle one delivered order now; the return window still applies. */
    settleSubOrder: (subOrderId: string) =>
      http
        .post<Settlement>(`/admin/settlements/sub-orders/${subOrderId}/settle`)
        .then((r) => r.data),
    runSettlement: () => http.post('/admin/settlements/run').then((r) => r.data),
    releaseReserves: () => http.post('/admin/settlements/release-reserves').then((r) => r.data),
    listRuns: () =>
      http
        .get<{ data: SettlementRunSummary[] }>('/admin/settlements/runs')
        .then((r) => r.data.data),

    /** Where the money is across every vendor, for the admin finance overview. */
    adminOverview: (query?: settlements.AdminFinanceOverviewQuery) =>
      http
        .get<{ data: settlements.AdminFinanceOverview }>('/admin/settlements/overview', {
          params: query,
        })
        .then((r) => r.data.data),

    /**
     * One page of sellers with money in the settlement pipeline.
     *
     * Server-side paginated, searched, filtered and sorted — the page never
     * holds more than one page of rows, and finding a seller no longer starts
     * with knowing their id.
     */
    sellerPayouts: (query?: Partial<settlements.SellerPayoutsQuery>) =>
      http
        .get<{ data: settlements.SellerPayoutsResponse }>('/admin/settlements/payouts', {
          params: query,
        })
        .then((r) => r.data.data),

    /** Settlement stage for a page of sub-orders. */
    orderStatuses: (subOrderIds: string[]) =>
      http
        .post<{ data: settlements.OrderSettlementStatus[] }>('/admin/settlements/order-status', {
          subOrderIds,
        })
        .then((r) => r.data.data),
    approvePayout: (payoutId: string, note?: string) =>
      http.post(`/admin/settlements/payouts/${payoutId}/approve`, { note }).then((r) => r.data),
    rejectPayout: (payoutId: string, reason: string) =>
      http.post(`/admin/settlements/payouts/${payoutId}/reject`, { reason }).then((r) => r.data),
    releasePayout: (
      payoutId: string,
      body: { bankRef?: string; provider?: 'RAZORPAY_X' | 'MANUAL' },
    ) => http.post(`/admin/settlements/payouts/${payoutId}/release`, body).then((r) => r.data),

    // COD reconciliation — cash the couriers hold and owe the platform. A leg is not
    // settleable to its vendor until it is marked remitted here.
    listPendingCodRemittances: (params?: { page?: number; pageSize?: number; vendorId?: string }) =>
      http
        .get<{
          data: PendingCodRemittance[];
          page: number;
          pageSize: number;
          total: number;
        }>('/admin/payments/cod/remittances', { params })
        .then((r) => r.data),
    markCodRemitted: (subOrderId: string, reference?: string) =>
      http
        .post(
          `/admin/payments/cod/remittances/${subOrderId}/remit`,
          reference ? { reference } : undefined,
        )
        .then((r) => r.data as PendingCodRemittance),
    /**
     * Match a courier remittance statement (AWB or order id, with amount) against
     * what is owed. `apply: false` only reports; `apply: true` marks the exact
     * matches received under `reference`.
     */
    reconcileCodRemittances: (body: {
      rows: Array<{ ref: string; amountPaise?: number }>;
      reference?: string;
      apply: boolean;
    }) =>
      http
        .post<CodReconcileResult>('/admin/payments/cod/remittances/reconcile', body)
        .then((r) => r.data),

    // Refunds owed to customers that did not go through on their own.
    listRefundTasks: (params?: {
      status?: 'OPEN' | 'RESOLVED';
      page?: number;
      pageSize?: number;
    }) =>
      http
        .get<{
          data: RefundTask[];
          page: { page: number; pageSize: number; total: number; pageCount: number };
        }>('/admin/payments/refund-tasks', { params })
        .then((r) => r.data),
    retryRefundTask: (taskId: string) =>
      http.post<RefundTask>(`/admin/payments/refund-tasks/${taskId}/retry`).then((r) => r.data),
    resolveRefundTask: (taskId: string, note: string) =>
      http
        .post<RefundTask>(`/admin/payments/refund-tasks/${taskId}/resolve`, { note })
        .then((r) => r.data),
  };
}

/** A refund a customer is owed that the system could not complete on its own. */
export interface RefundTask {
  id: string;
  orderGroupId: string;
  orderNumber?: string | null;
  customerName?: string | null;
  paymentId: string | null;
  /** Pay this UPI ID by hand (a COD return), then mark it paid. */
  payToUpiId?: string | null;
  amountPaise: number;
  source: 'ORDER_CANCELLED' | 'PACKAGE_CANCELLED' | 'RETURN' | 'LATE_PAYMENT' | 'GATEWAY_FAILED';
  reason: string;
  status: 'OPEN' | 'RESOLVED';
  attempts: number;
  lastError: string | null;
  resolutionNote: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

/** One delivered COD leg awaiting the courier's payment to the platform. */
export interface PendingCodRemittance {
  id: string;
  subOrderId: string;
  orderNumber: string;
  vendorId: string;
  vendorName: string;
  amountPaise: number;
  status: 'PENDING' | 'COLLECTED' | 'REMITTED' | 'RECONCILED';
  deliveredAt: string | null;
  collectedAt: string | null;
  /** The courier's tracking number — how its remittance report names the parcel. */
  awbNumber: string | null;
  courierName: string | null;
}

/** How one line of a courier remittance statement compares with what is owed. */
export type CodReconcileOutcome =
  | 'MATCHED'
  | 'AMOUNT_MISMATCH'
  | 'ALREADY_RECEIVED'
  | 'NOT_DELIVERED'
  | 'NOT_FOUND'
  | 'DUPLICATE';

export interface CodReconcileResult {
  rows: Array<{
    ref: string;
    amountPaise: number | null;
    outcome: CodReconcileOutcome;
    subOrderId: string | null;
    orderNumber: string | null;
    vendorName: string | null;
    awbNumber: string | null;
    expectedPaise: number | null;
    applied: boolean;
  }>;
  summary: {
    lines: number;
    matched: number;
    matchedPaise: number;
    applied: number;
    problems: number;
  };
}

export type SettlementsEndpoints = ReturnType<typeof settlementsEndpoints>;
